import React, { useEffect, useMemo, useReducer } from "react";
import {
  Badge,
  Card,
  Empty,
  List,
  Segmented,
  Space,
  Tag,
  Typography,
  Tooltip,
  Button,
  Skeleton,
  message,
  Row,
  Col,
} from "antd";
import {
  FiGrid,
  FiTarget,
  FiBell,
  FiUser,
  FiCreditCard,
  FiLayers,
  FiShield,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiCalendar,
  FiExternalLink,
} from "react-icons/fi";
import { Link, useLocation } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import { useSelector } from "react-redux";
import { initialState, reducer } from "./reducer";
import MemberServiceInstance from "../../services/MemberService";
import { isValidValue } from "../../helpers/FormHelper";

const { Text } = Typography;

function Subnav() {
  const LINKS = [
    { to: "/dashboard", label: "Dashboard", Icon: FiGrid },
    { to: "/my/predictions", label: "My predictions", Icon: FiTarget },
    { to: "/notifications", label: "Notifications", Icon: FiBell },
    { to: "/account/profile", label: "My profile", Icon: FiUser },
    { to: "/account/billing", label: "Billing", Icon: FiCreditCard },
    { to: "/plans", label: "Plans & Pricing", Icon: FiLayers },
    { to: "/account/security", label: "Change password", Icon: FiShield },
  ];
  const { pathname } = useLocation();
  const isActive = (to) => pathname === to || (to !== "/" && pathname.startsWith(to));

  return (
    <nav className="px-subnav" aria-label="Account navigation">
      <div className="px-subnav__inner">
        {LINKS.map(({ to, label, Icon }) => (
          <Link key={to} to={to} className={`px-subnav__link ${isActive(to) ? "is-active" : ""}`}>
            <Icon aria-hidden />
            <span>{label}</span>
          </Link>
        ))}
      </div>
    </nav>
  );
}

const STATUS_META = {
  CORRECT: { color: "green", icon: <FiCheckCircle />, label: "Correct" },
  INCORRECT: { color: "red", icon: <FiXCircle />, label: "Incorrect" },
  PENDING: { color: "default", icon: <FiClock />, label: "Pending" },
};

const optionLabel = (options = [], value) =>
  options.find((o) => o?.value === value)?.label || value || "—";

const fmtPct = (p) => (typeof p === "number" ? `${Math.round(p * 100)}%` : "—");
export const fmtKickoff = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const date = d.toLocaleDateString([], { year: "numeric", month: "short", day: "2-digit" });
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${date} • ${time}`;
};

const findLeagueName = (leagueId, coverageCompetitions) => {
  return coverageCompetitions?.find((item) => item?.competitionId === leagueId)?.name;
};


export default function MyPredictions() {
  const coverageLeagues = useSelector((state) => state.Utility.coverageCompetitions);
  const token = useSelector((state) => state.Auth.token);
  const [state, dispatch] = useReducer(reducer, initialState);

  const [tab, setTab] = React.useState("all");

  useEffect(() => {
    let isMounted = true;

    async function fetchPredictions() {
      try {
        dispatch({ type: "set_loading", payload: true });
        const res = await MemberServiceInstance.getDashboardCounters(token);
        const data = res?.data;
        if (data?.success) {
          dispatch({ type: 'set_prediction_counters', payload: data?.result?.counters });
        }
      } catch (err) {

      } finally {
        if (isMounted) dispatch({ type: "set_loading", payload: false });
      }
    }

    if (token) fetchPredictions();
    return () => {
      isMounted = false;
    };
  }, [token, state.filters]);

  useEffect(() => {
    let isMounted = true;

    async function fetchPredictions() {
      try {
        dispatch({ type: "set_loading", payload: true });
        const res = await MemberServiceInstance.getMyPredictions(token, state.filters);
        const data = res?.data;
        if (data?.success) {
          const list = Array.isArray(data.result?.data) ? data.result.data : [];
          const count = Number(data.result?.count || 0);

          const normalized = list.map((it) => {
            const fixture = it.fixture || {};
            const home = fixture.home || {};
            const away = fixture.away || {};
            return {
              ...it,
              _raw: it,
              id: it.uuid || `${fixture.apiId}-${it.marketCode}-${it.createDate}`,
              homeTeam: home.name || "Home",
              awayTeam: away.name || "Away",
              homeLogo: home.logo,
              awayLogo: away.logo,
              kickoff: fixture.scheduledDate,
              market: `${it.marketName} • ${optionLabel(it.options, it.bestValue)}`,
              probability: it.confidence,
              fixtureId: fixture.apiId,
            };
          });

          dispatch({ type: "set_items", payload: { items: normalized, count } });
        } else {
          dispatch({ type: "set_items", payload: { items: [], count: 0 } });
        }
      } catch (err) {
        dispatch({ type: "set_items", payload: { items: [], count: 0 } });
        message.error("Failed to load predictions.");
      } finally {
        if (isMounted) dispatch({ type: "set_loading", payload: false });
      }
    }

    if (token) fetchPredictions();
    return () => {
      isMounted = false;
    };
  }, [token, state.filters]);

  const counts = useMemo(() => {
    const all = state.predictionCounters.total;
    return {
      all,
      CORRECT: state.predictionCounters.correct,
      INCORRECT: state.predictionCounters.incorrect,
      PENDING: state.predictionCounters.pending,
    };
  }, [state.predictionCounters]);

  const onChangeTab = (val) => {
    setTab(val);
    dispatch({
      type: "set_search_filters",
      payload: { ...state.filters.searchFilters, status: val === "all" ? null : val },
    });
    dispatch({
      type: "set_table_pagination",
      payload: { currentPage: 1, pageSize: state.filters.pagination.pageSize },
    });
  };

  const onChangePage = (page, pageSize) => {
    dispatch({ type: "set_table_pagination", payload: { currentPage: page, pageSize } });
  };
  const onChangePageSize = (current, size) => {
    dispatch({ type: "set_table_pagination", payload: { currentPage: 1, pageSize: size } });
  };

  const ui = {
    heroWrap: {
      position: "relative",
      padding: "80px 16px 80px",
      background:
        "radial-gradient(1200px 280px at 50% -30%, rgba(59,130,246,0.25), transparent 60%), linear-gradient(180deg, #0b1020 0%, #0c1428 100%)",
      color: "white",
      borderBottom: "1px solid rgba(255,255,255,.06)",
    },
    heroInner: { maxWidth: 1350, margin: "0 auto" },
    badge: {
      display: "inline-flex", alignItems: "center", gap: 8,
      fontSize: 12, color: "#93c5fd", background: "rgba(147,197,253,.10)",
      border: "1px solid rgba(147,197,253,.30)", padding: "6px 12px", borderRadius: 999,
    },
    heroTitle: { margin: "14px 0 10px", fontSize: 36, lineHeight: 1.15, fontWeight: 900, color: "#fff", letterSpacing: "-0.02em" },
    heroSub: { margin: 0, color: "rgba(255,255,255,.85)", maxWidth: 820 },
  };

  return (
    <Layout headerStyle={1} footerStyle={1} breadcrumbTitle="My Predictions">
      <section style={ui.heroWrap} aria-label="Hero">
        <div style={ui.heroInner}>
          <span style={ui.badge}><FiTarget /> My Predictions</span>
          <h1 style={ui.heroTitle}>Your model predictions by result</h1>
          <p style={ui.heroSub}>
            Read-only record of your markets, confidence, and outcomes. Filter by result and drill into match pages.
          </p>
        </div>
      </section>

      <Subnav />

      <section className="px-preds">
        <div className="px-wrap px-wrap--onecol">
          <main className="px-main">
            <Card className="px-card" bordered={false}>
              <div className="px-toolbar">
                <Space wrap size={[8, 8]}>
                  <Segmented
                    value={tab}
                    onChange={onChangeTab}
                    options={[
                      {
                        label: (
                          <Row gutter={24}>
                            <Col span={24}>
                              <span>All</span>
                            </Col>
                            <Col span={24}>
                              <Badge count={state.predictionCounters.total} size="small" color="#004aad" />
                            </Col>
                          </Row>
                        ),
                        value: "all",
                      },
                      {
                        label: (
                          <Row gutter={24}>
                            <Col span={24}>
                              <span>Correct</span>
                            </Col>
                            <Col span={24}>
                              <Badge count={counts.CORRECT} size="small" color="#16a34a" />
                            </Col>
                          </Row>
                        ),
                        value: "correct",
                      },
                      {
                        label: (
                          <Row gutter={24}>
                            <Col span={24}>
                              <span>Incorrect</span>
                            </Col>
                            <Col span={24}>
                              <Badge count={counts.INCORRECT} size="small" color="#ef4444" />
                            </Col>
                          </Row>
                        ),
                        value: "incorrect",
                      },
                      {
                        label: (
                          <Row gutter={24}>
                            <Col span={24}>
                              <span>Pending</span>
                            </Col>
                            <Col span={24}>
                              <Badge count={counts.PENDING} size="small" color="#64748b" />
                            </Col>
                          </Row>
                        ),
                        value: "pending",
                      },
                    ]}
                  />
                </Space>

                <Space wrap>
                  <Tooltip title="Opens Predictions index">
                    <Link to="/predictions">
                      <Button type="default" icon={<FiExternalLink />}>Browse predictions</Button>
                    </Link>
                  </Tooltip>
                </Space>
              </div>

              <List
                loading={state.isLoading}
                itemLayout="vertical"
                dataSource={state.items}
                locale={{
                  emptyText: state.isLoading ? (
                    <Skeleton active />
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={<span>No predictions in this view.</span>}
                    />
                  ),
                }}
                pagination={{
                  current: state.filters.pagination.currentPage,
                  pageSize: state.filters.pagination.pageSize,
                  total: state.totalCount,
                  showSizeChanger: true,
                  pageSizeOptions: ["5", "10", "20", "50"],
                  responsive: true,
                  onChange: onChangePage,
                  onShowSizeChange: onChangePageSize,
                }}
                renderItem={(p) => {
                  const meta = STATUS_META[p.status] || STATUS_META.PENDING;
                  return (
                    <List.Item className="px-item" key={p.id}>
                      <div className={`px-item-card ${p.status.code === "pending" ? "is-pending" : ""}`}>
                        <div className="px-content">
                          <div className="px-toprow">
                            <div className="px-titlewrap">
                              <span className="px-title">
                                {p.homeTeam} <span className="px-vs">vs</span> {p.awayTeam}
                              </span>
                              <Tag className="px-tag">{findLeagueName(p?.fixture?.leagueId, coverageLeagues)}</Tag>
                            </div>
                          </div>
                          <div className="px-body">
                            <div className="px-kv">
                              <span className="px-k">Predicted</span>
                              <span className="px-v">
                                {p.market} <span className="px-chip">{fmtPct(p.probability)}</span>
                              </span>
                            </div>
                            <div className="px-kv">
                              <span className="px-k">Actual</span>
                              <span className="px-v">{isValidValue(p.fixture.homeGoals) ? `${p.fixture.homeGoals} - ${p.fixture.awayGoals}` || "—" : "—"}</span>
                            </div>
                          </div>
                          <div className="px-time">
                            <FiCalendar aria-hidden />
                            <Text type="secondary">{fmtKickoff(p.kickoff)}</Text>
                          </div>
                        </div>

                        <div className="px-actions">
                          <div className={`px-status px-${p?.status?.code}`}>
                            {meta.icon}
                            <span className="px-status-label">{p?.status?.name}</span>
                          </div>
                          <Link to={`/match/${p.fixtureId || ""}`} className="px-view">
                            <Button type="text" icon={<FiExternalLink />}>View match</Button>
                          </Link>
                        </div>
                      </div>
                    </List.Item>
                  );
                }}
              />
            </Card>
          </main>
        </div>

        <style>{`
          .px-preds { background:#f7f7f9; padding:30px; }
          .px-wrap { max-width: 1350px; margin: 0 auto; }
          .px-wrap--onecol { display:block; }
          @media (max-width: 520px) { .px-preds { padding:16px; } }

          .px-card {
            border-radius:18px;
            background:#fff;
            border:1px solid rgba(0,0,0,.06);
            box-shadow: 0 10px 30px rgba(0,0,0,.06);
          }

          .px-toolbar {
            display:flex; align-items:center; justify-content:space-between;
            gap:12px; margin-bottom:12px; flex-wrap:wrap;
          }

          .px-item { padding: 0; border: none; }
          .px-item-card {
            position: relative;
            display: grid;
            grid-template-columns: 1fr auto;
            grid-template-areas: "content actions";
            align-items: center;
            gap: 14px;
            padding: 14px;
            border-radius: 14px;
            border: 1px solid rgba(2,6,23,.06);
            background: #fff;
            box-shadow: 0 6px 16px rgba(2,6,23,.05);
            transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
          }
          .px-item-card:hover {
            transform: translateY(-1px);
            box-shadow: 0 12px 28px rgba(2,6,23,.08);
            border-color: rgba(2,6,23,.10);
          }
          .px-item-card.is-pending { background: linear-gradient(180deg,#fff,#fafafa); }

          .px-content { grid-area: content; display:flex; flex-direction:column; gap:6px; min-width:0; }
          .px-toprow { display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; }
          .px-titlewrap { display:flex; align-items:center; gap:10px; flex-wrap:wrap; min-width:0; }
          .px-title { font-weight:800; color:#0f172a; font-size:16px; line-height:1.25; overflow-wrap:anywhere; }
          .px-vs { color:#64748b; font-weight:700; margin: 0 4px; }
          .px-tag { border-radius:999px; }

          .px-time { display:flex; align-items:center; gap:8px; white-space:nowrap; }
          .px-time svg { opacity:.7; }

          .px-body { display:grid; gap:6px; grid-template-columns: 1fr 1fr; }
          .px-kv { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
          .px-k { font-weight:700; color:#0f172a; min-width:72px; }
          .px-v { color:#334155; }
          .px-chip { margin-left:8px; font-size:12px; padding:2px 8px; border-radius:999px; background:#f1f5f9; border:1px solid rgba(2,6,23,.08); }

          .px-actions { grid-area: actions; display:flex; align-items:center; gap:8px; }
          .px-status { display:inline-flex; align-items:center; gap:6px; padding:6px 10px; border-radius:999px; font-weight:700; font-size:12px; border:1px solid transparent; }
          .px-status svg { margin-top: -1px; }
          .px-status.px-correct   { background: rgba(22,163,74,.10); color:#166534; border-color: rgba(22,163,74,.25); }
          .px-status.px-incorrect { background: rgba(239,68,68,.10); color:#7f1d1d; border-color: rgba(239,68,68,.25); }
          .px-status.px-pending   { background: rgba(100,116,139,.10); color:#334155; border-color: rgba(100,116,139,.25); }

          @media (max-width: 720px) {
            .px-item-card { grid-template-columns: 1fr; grid-template-areas: "content" "actions"; gap: 10px; padding: 12px 10px; }
            .px-body { grid-template-columns: 1fr; }
            .px-actions { justify-content: flex-end; }
          }

          .px-subnav { position: sticky; top: 0; z-index: 20; backdrop-filter: blur(6px); background: rgba(255,255,255,.85); border-bottom: 1px solid rgba(2,6,23,.08); }
          .px-subnav__inner { max-width: 1400px; margin: 0 auto; padding: 10px 16px; display: flex; gap: 8px; align-items: center; overflow-x: auto; }
          .px-subnav__link { display: inline-flex; align-items: center; padding: 8px 12px; gap: 8px; border-radius: 999px; font-size: 13px; font-weight: 700; color: #334155; text-decoration:none; border: 1px solid transparent; white-space: nowrap; }
          .px-subnav__link:hover { background: #f1f5f9; border-color: rgba(2,6,23,.08); }
          .px-subnav__link.is-active { color: #1e3a8a; background: rgba(37,99,235,.10); border-color: rgba(37,99,235,.25); }
        `}</style>
      </section>
    </Layout>
  );
}
